// The Money-typed buyer's-premium schedule (ADR 060 — Money Type) the calculator
// prices with, and the one adapter from the stored JSONB shape into it.

import Decimal from 'decimal.js';
import { Money } from '../money/money';
import type { SupportedCurrency } from '../money/currency';
import { Rate } from '../rate/rate';
import { activeBound, isUsableBuyersPremium, type BuyersPremiumCombineMode, type BuyersPremiumLike } from './types';

/** One band of a tiered schedule. `threshold` is inclusive; `null` means no upper bound. */
export interface PremiumTier {
  rate: Rate;
  threshold: Money | null;
}

/** Floor and cap, applied once per lot after the tiers. `null` means none. */
interface PremiumBounds {
  minFee: Money | null;
  maxFee: Money | null;
}

/**
 * A buyer's-premium schedule in exact types: rates are `Rate`, every dollar
 * figure is `Money` in the schedule's currency. This is the shape an invoice
 * version copies (ADR 032 — Fee Cascade, 2026-09-29 amendment) and the shape
 * {@link calculateBuyersPremium} reads.
 */
export type PremiumSchedule =
  | ({ type: 'Flat'; rate: Rate } & PremiumBounds)
  | ({ type: 'Tiered'; combineMode: BuyersPremiumCombineMode; tiers: PremiumTier[] } & PremiumBounds);

/**
 * Lifts a stored (JSONB, number-typed) buyer's-premium config into a
 * {@link PremiumSchedule} in the given currency. A structurally unusable config
 * (absent, tier-less, rate-less) maps to `null`, which prices to zero — the same
 * answer the number calculator gave. A non-positive `minFee` / `maxFee` means
 * "no bound", per {@link activeBound}.
 *
 * @param bp - The stored config, as read from a row or an editor draft.
 * @param currency - The currency the schedule's dollar figures are in.
 * @returns The exact schedule, or `null` when there is nothing to charge.
 */
export function premiumScheduleFromStored(
  bp: BuyersPremiumLike,
  currency: SupportedCurrency,
): PremiumSchedule | null {
  if (!isUsableBuyersPremium(bp)) return null;
  const money = (value: number | null | undefined): Money | null => {
    const bound = activeBound(value);
    return bound == null ? null : Money.of(new Decimal(bound), currency);
  };
  const bounds = { minFee: money(bp.minFee), maxFee: money(bp.maxFee) };
  if (bp.type === 'Flat') {
    return { type: 'Flat', rate: Rate.fromPercent(bp.flatRate), ...bounds };
  }
  return {
    type: 'Tiered',
    // BL-990 stamped combineMode on every stored tiered row; an in-flight
    // editor draft may still omit it, and absent has always meant Marginal.
    combineMode: bp.combineMode ?? 'Marginal',
    tiers: bp.tiers.map((t) => ({
      rate: Rate.fromPercent(t.rate),
      // `null` is the stored form of "no upper bound"; an in-memory schedule may
      // still carry `Infinity`, which has always meant the same thing.
      threshold: t.threshold == null || !Number.isFinite(t.threshold) ? null : Money.of(new Decimal(t.threshold), currency),
    })),
    ...bounds,
  };
}
